import { useState } from "react";
import { keyframes } from "@emotion/react";
import { Box, Stack } from "@mui/material";
import { alpha } from "@mui/material/styles";

const shake = keyframes`
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
`;

const blink = keyframes`
  50% { opacity: 0; }
`;

// Typing always appends; select-all is allowed so the whole code can be replaced or cleared
function keepCaretAtEnd(event) {
  const { selectionStart, selectionEnd, value } = event.target;
  const selectsAll = selectionStart === 0 && selectionEnd === value.length;
  if (!selectsAll && selectionStart !== value.length) event.target.setSelectionRange(value.length, value.length);
}

// One real input drawn as separate digit boxes, so paste, SMS autofill, backspace and
// screen readers behave like a normal field
export default function OtpInput({
  length,
  value,
  onChange,
  onComplete,
  error = false,
  readOnly = false,
  autoFocus = false,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const activeIndex = Math.min(value.length, length - 1);

  function handleChange(event) {
    const next = event.target.value.replace(/\D/g, "").slice(0, length);
    if (next === value) return;

    onChange(next);
    if (next.length === length) onComplete?.(next);
  }

  return (
    <Box
      sx={{
        position: "relative",
        animation: error ? `${shake} 350ms ease-in-out` : "none",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Stack direction="row" spacing={1} justifyContent="center" aria-hidden="true">
        {Array.from({ length }, (_, index) => {
          const isActive = isFocused && !readOnly && index === activeIndex;

          return (
            <Box
              key={index}
              sx={{
                // Shrink to fit narrow cards/phones, but don't grow into wide boxes
                flex: "1 1 0",
                minWidth: 0,
                maxWidth: 52,
                height: { xs: 52, sm: 56 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: 2,
                borderRadius: 2,
                borderColor: error ? "error.main" : isActive ? "primary.main" : "divider",
                boxShadow: (theme) =>
                  isActive && !error ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : "none",
                bgcolor: "background.paper",
                color: "text.primary",
                fontSize: 24,
                fontWeight: 600,
                transition: "border-color 150ms ease, box-shadow 150ms ease",
              }}
            >
              {value[index] ||
                (isActive && (
                  <Box
                    sx={{
                      width: 2,
                      height: 24,
                      bgcolor: "primary.main",
                      animation: `${blink} 1s step-end infinite`,
                    }}
                  />
                ))}
            </Box>
          );
        })}
      </Stack>

      <Box
        component="input"
        value={value}
        onChange={handleChange}
        onSelect={keepCaretAtEnd}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        readOnly={readOnly}
        autoFocus={autoFocus}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        spellCheck={false}
        aria-label={`${length}-digit verification code`}
        aria-invalid={error}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          p: 0,
          border: 0,
          outline: 0,
          background: "transparent",
          color: "transparent",
          caretColor: "transparent",
          // 16px stops iOS zooming into the field
          fontSize: 16,
          cursor: "text",
          "&::selection": { background: "transparent" },
        }}
      />
    </Box>
  );
}
